'use-strict';

import 'core-js/features/array/at';

import PCHelpers from './postcodeCheckHelpers';
import VZSpinner from 'Components/VZSpinner';
import { toggleButtonState } from 'Root/util/util';
import CONST from 'Util/constants';

export default class AddressCheck {
    constructor(availabilityForm, additionSelectElement) {
        this.availabilityForm = availabilityForm;
        this.additionSelectElement = additionSelectElement;
        this.additionContainerElement = this.availabilityForm.querySelector('.js-postcode-check-addition-container');
        this.roomContainerElement = this.availabilityForm.querySelector('.js-postcode-check-room-container');
        this.roomSelectElement = this.roomContainerElement?.querySelector('.js-postcode-check-room');
        this.roomLabelElement = this.roomContainerElement?.querySelector('.js-postcode-check-room-label');
        this.addressCheckEndpoint = this.availabilityForm.dataset?.addressCheckUrl;
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.postalCode = null;
        this.houseNumber = null;
        this.addition = null;

        // Hook up a change event to the addition select field so we can grab the PAID to do the availability check using that
        this.additionSelectElement.addEventListener('change', this.handleAdditionOnChange.bind(this));
        // Hook up events to the room select field
        if (this.roomSelectElement) {
            this.roomSelectElement.addEventListener('change', this.handleRoomOnChange.bind(this));
        }
        [...this.availabilityForm.querySelectorAll('input')].forEach((input) => input.addEventListener('blur', this.handleAddressCheckOnBlur.bind(this)));
        this.executeAddressCheckOnLoad();
    }

    /**
     * @function sortAddressExtensions
     * @description Sorts the addresses based on the extension. Manipulates the existing array.
     * @param {Array} addressInfoArray - The array of address information returned from the addressCheck endpoint.
     */
    static sortAddressExtensions(addressInfoArray) {
        addressInfoArray.sort((a, b) => (a.extension.toLowerCase() < b.extension.toLowerCase() ? -1 : 0));

        // Get the index of the no extension array item.
        const indexOfNoExtension = addressInfoArray.indexOf(addressInfoArray.find((address) => !address.extensionRequired));
        if (indexOfNoExtension >= 0) {
            //  Move the no extension item to the beginning of the array, and delete it in the original location, only if it exists.
            addressInfoArray.splice(0, 0, addressInfoArray.splice(indexOfNoExtension, 1)[0]);
        }
    }

    /**
     * @function removeAdditionOptions
     * @description Empties the additionSelectElement except for the default option. Makes the default selected.
     * @param {HTMLOptionsCollection} options - The additionSelectElement options.
     */
    removeAdditionOptions(options) {
        const optionsArray = [...options];
        const defaultOption = optionsArray.find((option) => option.hasAttribute('default'));

        optionsArray.filter((option) => !option.hasAttribute('default')).forEach((option) => option.remove()); // Remove options.
        this.additionSelectElement.selectedIndex = optionsArray.indexOf(defaultOption); // Set the default value as selected.
        this.additionSelectElement.classList.remove(CONST.classes.valid); // Reset the validity state.
    }

    /**
     * @function generateAdditionElementOptions
     * @description Generates the option elements to populate the additionSelectElement based on the address info passed in.
     * @param {Array} addressInfo - The array of address information returned from the addressCheck endpoint.
     * @param {boolean} hasMultipleExtensions - A boolean determining whether the address entered by the user has more extensions than 'No extension'
     * @param {string} roomSelectionMessage - The message to show when rooms need to be selected.
     */
    generateAdditionElementOptions(addressInfo, hasMultipleExtensions, roomSelectionMessage = null) {
        // Store room selection message for later use
        if (roomSelectionMessage) {
            this.availabilityForm.dataset.roomSelectionMessage = roomSelectionMessage;
        }
        // skips if there is already additions selected
        if (this.addition !== null) {
            return;
        }

        // Check if the select element has a value attribute set (e.g., from form attributes on page load)
        const initialValue = this.additionSelectElement.getAttribute('value') || this.additionSelectElement.value;

        this.removeAdditionOptions(this.additionSelectElement.options); // Prevent duplicate options from being generated.
        AddressCheck.sortAddressExtensions(addressInfo); // Sort alphabetically based on extensions.

        const hasSingleAdditionWithRooms = addressInfo.length === 1 && addressInfo[0].hasMultipleRooms;

        // Generate option elements based on address info returned from fetch.
        this.additionSelectElement.innerHTML = addressInfo.reduce((result, address, index) => {
            const { extension, extensionRequired, hasMultipleRooms } = address;

            // For addresses with multiple rooms, we need to handle them differently
            if (hasMultipleRooms) {
                // Store the rooms data on the option for later use
                const roomsData = JSON.stringify(address.rooms).replace(/'/g, '&apos;');
                return `${result}
                    <option 
                        value="${extensionRequired ? extension : ''}" 
                        data-has-multiple-rooms="true"
                        data-rooms="${encodeURI(roomsData)}"
                        ${!hasMultipleExtensions && (index === 0 || extension === this.addition) ? 'selected' : ''}>
                        ${extension}
                    </option>
                `;
            }

            // Handle normal addresses without rooms
            const { paid, formattedAddress, addressParts } = address;
            const stringifiedAddressParts = JSON.stringify(addressParts).replace(/'/g, '&apos;');

            return `${result}
                <option 
                    data-paid="${paid}"
                    value="${extensionRequired ? extension : ''}" 
                    data-formatted-address="${formattedAddress}"
                    data-address-parts="${encodeURI(stringifiedAddressParts)}"
                    ${!hasMultipleExtensions && (index === 0 || extension === this.addition) ? 'selected' : ''}>
                    ${extension}
                </option>
            `;
        }, this.additionSelectElement.innerHTML);

        let hasHandledPreselection = false;

        // If an initial value was set on the select element, select the matching option
        if (initialValue && initialValue !== '') {
            const matchingOption = [...this.additionSelectElement.options].find((option) => option.value === initialValue && !option.hasAttribute('default'));
            if (matchingOption) {
                matchingOption.selected = true;
                this.addition = initialValue;
                hasHandledPreselection = true;
                setTimeout(() => {
                    this.additionSelectElement.dispatchEvent(new Event('change'));
                }, 0);
            }
        }

        // Handle single addition with rooms case
        if (hasSingleAdditionWithRooms) {
            // Validate and hide the addition field
            if (this.additionContainerElement && this.additionSelectElement) {
                this.additionSelectElement.classList.add(CONST.classes.valid);
                this.additionContainerElement.classList.add(CONST.classes.dNone);
            }

            // Change room label to "Addition/room"
            if (this.roomLabelElement) {
                const additionRoomLabel = this.roomLabelElement.getAttribute('data-addition-room-label');
                this.roomLabelElement.textContent = additionRoomLabel;
            }

            // Auto-select the single addition option and trigger room display
            if (!hasHandledPreselection) {
                const firstOption = this.additionSelectElement.querySelector('option:not([default])');
                if (firstOption) {
                    firstOption.selected = true;
                    setTimeout(() => {
                        this.additionSelectElement.dispatchEvent(new Event('change'));
                    }, 0);
                }
            }
        } else {
            // Show addition field for normal cases
            if (this.additionContainerElement) {
                this.additionContainerElement.classList.remove(CONST.classes.dNone);
            }

            // Reset room label to default using data attribute
            if (this.roomLabelElement) {
                const defaultLabel = this.roomLabelElement.getAttribute('data-default-label');
                this.roomLabelElement.textContent = defaultLabel;
            }

            // Check if any option was auto-selected and trigger the change event
            if (!hasHandledPreselection) {
                const preselectedOption = [...this.additionSelectElement.options].find((option) => option.selected && !option.hasAttribute('default'));
                if (preselectedOption) {
                    setTimeout(() => {
                        this.additionSelectElement.dispatchEvent(new Event('change'));
                    }, 0);
                }
            }
        }
    }

    /**
     * @function handleAdditionOnChange
     * @description Grabs the PAID to be used for the availability check. Sets the address to be used when the availability service returns
     * 'unableToOrder = true'. Validates the additionSelectElement and enables the CTA.
     * @param {Event} event - The change event fired from the additionSelectElement.
     */
    handleAdditionOnChange(event) {
        const { target: additionSelectElement } = event;
        const selectedExtensionOption = [...additionSelectElement.options].find((option) => option.selected);
        const hasMultipleRooms = selectedExtensionOption.dataset.hasMultipleRooms === 'true';

        this.addition = selectedExtensionOption.value; // set addition to stop the regeneration of additions dropdown

        if (hasMultipleRooms) {
            // Handle addresses with multiple rooms
            // Get room selection message from the current address check response
            const roomSelectionMessage = this.availabilityForm.dataset?.roomSelectionMessage;
            this.handleMultipleRooms(selectedExtensionOption, roomSelectionMessage);
        } else {
            // Hide and reset room selector when switching to non-room address
            if (this.roomContainerElement) {
                this.roomContainerElement.classList.add(CONST.classes.dNone);
            }
            if (this.roomSelectElement) {
                this.removeRoomOptions(this.roomSelectElement.options);
            }

            // Remove the room selection message when switching to non-room address
            PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');

            // Handle normal addresses without rooms
            const { paid: addressCheckPAID, formattedAddress: addressCheckAddress, addressParts } = selectedExtensionOption.dataset;
            this.availabilityForm.setPaid(addressCheckPAID);

            // Update the address on change so its up to date for use in fn handleUnableToOrder. This is used to show the address in the availability check response.
            this.availabilityForm.addressCheckAddress = addressCheckAddress;

            // Provide the availability check element with the parts of the address so it can be used when pushing errors to the dataLayer. See fn pushAvailabilityCheckErrorsToDataLayer().
            // This is also used to show addresses in the sidebar for One Net where there is no availability.
            this.availabilityForm.addressCheckAddressParts = JSON.parse(decodeURI(addressParts));

            // If the user has selected a value, mark the field as valid and enable the CTA to perform the availability check.
            if (additionSelectElement.checkValidity()) {
                additionSelectElement.classList.add(CONST.classes.valid);

                // Enable the CTA once a selection has been made.
                toggleButtonState(this.availabilityForm.submitButton, true);
            }
        }

        additionSelectElement.setAttribute('value', event.target.value);
    }

    /**
     * @function handleMultipleRooms
     * @description Handles addresses that have multiple rooms by populating the room selector.
     * @param {HTMLOptionElement} selectedExtensionOption - The selected extension option element.
     * @param {string} roomSelectionMessage - The message to show when rooms need to be selected.
     */
    handleMultipleRooms(selectedExtensionOption, roomSelectionMessage = null) {
        const roomsData = JSON.parse(decodeURI(selectedExtensionOption.dataset.rooms));

        if (this.roomSelectElement) {
            // Check if the room select element has a value attribute set
            const initialRoomValueAttr = this.roomSelectElement.getAttribute('value');
            const initialRoomValue = initialRoomValueAttr && initialRoomValueAttr !== '' ? initialRoomValueAttr : this.roomSelectElement.value;

            // Store the currently selected room value before clearing options
            const currentlySelectedRoom = initialRoomValue;

            // Clear existing room options except the default
            this.removeRoomOptions(this.roomSelectElement.options);

            // Populate room options
            this.roomSelectElement.innerHTML = roomsData.reduce((result, room) => {
                const stringifiedAddressParts = JSON.stringify(room.addressParts).replace(/'/g, '&apos;');
                const isSelected = currentlySelectedRoom === room.room ? 'selected' : '';

                return `${result}
                    <option 
                        data-paid="${room.paid}"
                        value="${room.room}" 
                        data-formatted-address="${room.formattedAddress}"
                        data-address-parts="${encodeURI(stringifiedAddressParts)}"
                        ${isSelected}>
                        ${room.roomLabel}
                    </option>
                `;
            }, this.roomSelectElement.innerHTML);

            // Show the room selector and handle room selection logic
            if (this.roomContainerElement) {
                this.roomContainerElement.classList.remove(CONST.classes.dNone);
            }
            this.roomSelectElement.removeAttribute('disabled');

            // Remove any existing messages and show room selection message only if no room is selected
            PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');
            if (roomSelectionMessage && !currentlySelectedRoom) {
                PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(roomSelectionMessage, true);
            }

            // Enable/disable submit button based on room selection and update validity state
            const hasRoomSelected = currentlySelectedRoom && currentlySelectedRoom !== '';
            if (hasRoomSelected) {
                this.roomSelectElement.classList.add(CONST.classes.valid);
                // Trigger change event to update PAID and address data for preselected room
                this.roomSelectElement.dispatchEvent(new Event('change'));
            }
            toggleButtonState(this.availabilityForm.submitButton, hasRoomSelected);
        }
    }

    /**
     * @function handleRoomOnChange
     * @description Handles room selection and sets the PAID for availability check.
     * @param {Event} event - The change event fired from the roomSelectElement.
     */
    handleRoomOnChange(event) {
        const { target: roomSelectElement } = event;
        const selectedRoomOption = [...roomSelectElement.options].find((option) => option.selected);

        if (selectedRoomOption && !selectedRoomOption.hasAttribute('default')) {
            const { paid: addressCheckPAID, formattedAddress: addressCheckAddress, addressParts } = selectedRoomOption.dataset;
            this.availabilityForm.setPaid(addressCheckPAID);

            // Update the address on change so its up to date for use in fn handleUnableToOrder
            this.availabilityForm.addressCheckAddress = addressCheckAddress;

            // Provide the availability check element with the parts of the address
            this.availabilityForm.addressCheckAddressParts = JSON.parse(decodeURI(addressParts));

            // If the user has selected a room, mark the field as valid and enable the CTA
            if (roomSelectElement.checkValidity()) {
                roomSelectElement.classList.add(CONST.classes.valid);
                toggleButtonState(this.availabilityForm.submitButton, true);

                // Remove the room selection message since a room has been selected
                PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');
            }

            roomSelectElement.setAttribute('value', event.target.value);
        }
    }

    /**
     * @function removeRoomOptions
     * @description Empties the roomSelectElement except for the default option. Makes the default selected.
     * @param {HTMLOptionsCollection} options - The roomSelectElement options.
     */
    removeRoomOptions(options) {
        const optionsArray = [...options];
        const defaultOption = optionsArray.find((option) => option.hasAttribute('default'));

        optionsArray.filter((option) => !option.hasAttribute('default')).forEach((option) => option.remove());
        if (this.roomSelectElement && defaultOption) {
            this.roomSelectElement.selectedIndex = optionsArray.indexOf(defaultOption);
            this.roomSelectElement.classList.remove(CONST.classes.valid);
        }
    }

    /**
     * @function handleAddressCheckOnBlur
     * @description Executes the address check on blur of the postcode and/or house number field, given that both have a valid value.
     * @param {Event} event - The blur event.
     */
    handleAddressCheckOnBlur() {
        const { postcodeInputElement, houseNumberInputElement } = this.availabilityForm;
        // checks if there was anything changed since previous time
        if (this.postalCode !== postcodeInputElement.value || this.houseNumber !== houseNumberInputElement.value) {
            this.addition = null; // resets additions after a change in postal or houseNumber
            this.postalCode = postcodeInputElement.value;
            this.houseNumber = houseNumberInputElement.value;

            // Hide and reset room selector when address changes
            if (this.roomContainerElement) {
                this.roomContainerElement.classList.add(CONST.classes.dNone);
            }
            if (this.roomSelectElement) {
                this.removeRoomOptions(this.roomSelectElement.options);
            }

            // Remove any existing room selection messages when address changes
            PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');

            // Reset addition field visibility and room label
            if (this.additionContainerElement) {
                this.additionContainerElement.classList.remove(CONST.classes.dNone);
            }
            if (this.roomLabelElement) {
                const defaultLabel = this.roomLabelElement.getAttribute('data-default-label');
                this.roomLabelElement.textContent = defaultLabel;
            }

            const isMinimumRequirementMet = [postcodeInputElement, houseNumberInputElement].every((element) => element.value && element.checkValidity());
            if (isMinimumRequirementMet) {
                this.executeAddressCheck().catch((error) => PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(error)); // Handle rejection from promise returned in fn executeAddressCheck.
            }
        }
    }

    /**
     * @function handleAddressNotFound
     * @description Handles the scenario where no address is found.
     */
    handleAddressNotFound() {
        this.removeAdditionOptions(this.additionSelectElement.options); // Reset the additionSelectElement when an address is not found.

        // Also reset room selector
        if (this.roomContainerElement) {
            this.roomContainerElement.classList.add(CONST.classes.dNone);
        }
        if (this.roomSelectElement) {
            this.removeRoomOptions(this.roomSelectElement.options);
        }

        // Reset addition field visibility and room label
        if (this.additionContainerElement) {
            this.additionContainerElement.classList.remove(CONST.classes.dNone);
        }
        if (this.roomLabelElement) {
            const defaultLabel = this.roomLabelElement.getAttribute('data-default-label');
            this.roomLabelElement.textContent = defaultLabel;
        }

        // Disable the addition field when no address is found
        this.additionSelectElement.setAttribute('disabled', 'disabled');

        toggleButtonState(this.availabilityForm.submitButton, false);
        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');
    }

    /**
     * @function handleMultipleAdditions
     * @description When the address check responds with multiple additions for a postcode and house number,
     * this will generate options and append them to the additionSelectElement.
     * @param {Object} addressCheckResponseData - The JSON response returned from the address check.
     */
    handleMultipleAdditions(addressCheckResponseData) {
        const { addressInfo, hasMultipleExtensions, roomSelectionMessage } = addressCheckResponseData;
        const { additionHelpText } = this.availabilityForm.dataset;

        if (!this.addition) {
            this.additionSelectElement.classList.remove(CONST.classes.valid); // Reset the validity state of the addition element
        }
        this.generateAdditionElementOptions(addressInfo, hasMultipleExtensions, roomSelectionMessage); // Populate the additions field.
        this.additionSelectElement.removeAttribute('disabled'); // In case the field was previously disabled, enable it again.
        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message');

        // Only show addition help text if we actually have multiple extensions to choose from
        // Don't show it if we have a single addition with multiple rooms
        // Don't show it if the addition field already has a valid selection
        const hasSingleAdditionWithRooms = addressInfo.length === 1 && addressInfo[0].hasMultipleRooms;
        const hasValidAdditionSelection = this.additionSelectElement.classList.contains(CONST.classes.valid);
        if (hasMultipleExtensions && !hasSingleAdditionWithRooms && !hasValidAdditionSelection) {
            PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(additionHelpText, false); // Show a help message when there are multiple extensions
        }

        if (!this.addition) {
            toggleButtonState(this.availabilityForm.submitButton, false); // Disable the CTA until a selection has been made
        }
    }

    /**
     * @function handleSuccessfulAddressCheck
     * @description Handles the successful addressCheck scenario.
     * @param {Object} addressCheckResponseData - The JSON response returned from the address check.
     */
    handleSuccessfulAddressCheck(addressCheckResponseData) {
        const { formattedAddress = null, hasMultipleExtensions, addressInfo, roomSelectionMessage } = addressCheckResponseData;

        // Update the address on change so its up to date for use in fn handleUnableToOrder
        this.availabilityForm.addressCheckAddress = formattedAddress;
        this.generateAdditionElementOptions(addressInfo, hasMultipleExtensions, roomSelectionMessage); // Populate the additions field.

        // Disable the field when there are no extensions apart from 'No Extension'.
        if (!hasMultipleExtensions && !addressInfo.extensionRequired) {
            this.additionSelectElement.setAttribute('disabled', 'disabled');
            this.additionSelectElement.dispatchEvent(new Event('change'));
        } else if (!hasMultipleExtensions) {
            // Single extension case - ensure change event is triggered for preselected option
            const preselectedOption = [...this.additionSelectElement.options].find((option) => option.selected && !option.hasAttribute('default'));
            if (preselectedOption) {
                this.additionSelectElement.dispatchEvent(new Event('change'));
            }
        }

        PCHelpers.removeExistingFeedbackMessage(this.availabilityForm, '.js-availability-address-check-message, .error-msg');
    }

    /**
     * @function executeAddressCheck
     * @description Executes the address check.
     * @returns {Promise} A promise determining whether the address check was succesful or not. Rejected when address is not found,
     * resolved in other scenarios so the availability check can be performed.
     */
    executeAddressCheck() {
        this.spinner.start();

        /**
         * BO-1570 - To make sure that the availability check is only executed for found addresses, a promise needs to be returned and
         * rejected when the address is not found so that the availability check is not performed.
         */
        return new Promise((resolve, reject) => {
            PCHelpers.fetchAddressAndAvailability(this.addressCheckEndpoint, this.availabilityForm)
                .then((addressCheckResponseData) => {
                    const multipleExtensionsFound = addressCheckResponseData?.multipleExtensionsFound;

                    if (!addressCheckResponseData?.success && multipleExtensionsFound) {
                        this.handleMultipleAdditions(addressCheckResponseData);
                        resolve();
                    } else if (!addressCheckResponseData?.success) {
                        this.handleAddressNotFound();
                        reject(addressCheckResponseData?.serverErrors[0]);
                    } else {
                        this.handleSuccessfulAddressCheck(addressCheckResponseData);
                        resolve();
                    }
                })
                .catch(PCHelpers.handleErrors.bind(this))
                .finally(this.spinner.stop);
        });
    }

    /**
     * @function executeAddressCheckOnLoad
     * @description Executes the address check on load if values are present. This is mainly so the extension list is populated already,
     * and that the CTA is enabled/disabled based on the status of the address check.
     */
    executeAddressCheckOnLoad() {
        const { postcodeInputElement, houseNumberInputElement } = this.availabilityForm;

        if (postcodeInputElement.checkValidity() && houseNumberInputElement.checkValidity()) {
            // Initialize stored values to prevent unnecessary re-execution on blur
            this.postalCode = postcodeInputElement.value;
            this.houseNumber = houseNumberInputElement.value;

            this.executeAddressCheck().catch((error) => PCHelpers.showFeedbackMessage.bind(this.availabilityForm)(error)); // Handle rejection from promise returned in fn executeAddressCheck.
        }
    }
}
